<template>
    <div class="screensaver-images">
        <div v-for="(row, index) in rows" :key="row.key" class="screensaver-item">
            <div class="imagepicker">
                <label v-if="!row.image" class="preview">
                    <div class="actbtn"><i class="la la-plus"></i></div>
                    <div class="hint">
                        <div class="label">{{ i18n("elements.imagePicker.noImage") }}</div>
                    </div>
                    <input type="file" @change="onFileChange(row, $event)" />
                </label>
                <div v-else class="preview">
                    <img :src="row.image.imagePath" alt="" />
                </div>
            </div>

            <div class="item-actions">
                <button type="button" class="btn btn-sm btn-success" @click="addRow(index)">
                    <i class="la la-plus"></i>
                </button>
                <button type="button" class="btn btn-sm btn-danger" @click="removeRow(row)">
                    <i class="la la-trash"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { LocationDao } from "@/data/dao/service/LocationDao";
    import type { IScreenSaverImage } from "@/data/model/Location";
    import { Misc } from "@/data/util/Misc";
    import { logError } from "@/shared/logging/errors";
    import { v4 as uuidv4 } from "uuid";
    import { ref } from "vue";

    import { i18n } from "@/portal/Trans";
    import { UiNotification } from "@/portal/ui/Notification";

    interface IEditorRow {
        key: string;
        image?: IScreenSaverImage;
    }

    const dao = Factory.getInstance<LocationDao>(LocationDao);

    const rows = ref<IEditorRow[]>([]);
    let locationId: string | undefined;

    function setLocation(id?: string) {
        locationId = id;
        rows.value = [];

        if (id !== undefined) load();
        else ensureAtLeastOneRow();
    }

    async function load() {
        try {
            const images = await dao.getScreenSaverImages(locationId!);
            rows.value = images.map((image) => ({ key: uuidv4(), image }));
        } catch (ex) {
            logError(ex);
        } finally {
            ensureAtLeastOneRow();
        }
    }

    function ensureAtLeastOneRow() {
        if (rows.value.length === 0) rows.value.push({ key: uuidv4() });
    }

    function addRow(index: number) {
        rows.value.splice(index + 1, 0, { key: uuidv4() });
    }

    async function removeRow(row: IEditorRow) {
        if (row.image !== undefined) {
            try {
                await dao.deleteScreenSaverImage(locationId!, row.image.id);
            } catch (ex) {
                logError(ex);
                UiNotification.showException(i18n("elements.imagePicker.uploadFailed"));
                return;
            }
        }

        rows.value = rows.value.filter((r) => r.key !== row.key);
        ensureAtLeastOneRow();
    }

    async function onFileChange(row: IEditorRow, event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file === undefined) return;

        if (validate(file)) {
            try {
                row.image = await dao.addScreenSaverImage(locationId!, row.image?.id, file);
            } catch (ex) {
                logError(ex);
                UiNotification.showException(i18n("elements.imagePicker.uploadFailed"));
            }
        }

        input.value = "";
    }

    function validate(file: File): boolean {
        const maxAllowedFileSizeInBytes = 5000000; //in bytes 5mb

        if (!file.type.includes("image")) {
            UiNotification.showException(i18n("elements.imagePicker.pleaseSelectImage"));
            return false;
        }

        if (file.size > maxAllowedFileSizeInBytes) {
            UiNotification.showException(
                i18n("elements.imagePicker.invalidSize", { size: Misc.byteToReadableString(maxAllowedFileSizeInBytes) })
            );
            return false;
        }

        return true;
    }

    defineExpose({
        setLocation,
    });
</script>

<style scoped lang="scss">
    .screensaver-images {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
    }

    .screensaver-item {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .item-actions {
        display: flex;
        justify-content: center;
        gap: 8px;
    }

    input[type="file"] {
        display: none;
    }
</style>
